// src/pages/Properties/PropertyCreatePage.tsx - Fixed version
import {useState} from "react";
import {useNavigate} from "react-router-dom";
import {Save, X, MapPin, Upload, Image} from "lucide-react";
import {FormField, PageHeader} from "../../components/common";
import {Button, Card, Badge} from "../../components/ui";
import {useToast} from "../../hooks/common/useToast";
import {useProperties} from "../../hooks/useProperties";
import {useRegions} from "../../hooks/useRegions";
import {usePropertyTypes} from "../../hooks/usePropertyTypes";
import {ROUTES} from "../../utils/constants";
import type {
  CreatePropertyDto,
  ListingType,
  DevelopmentType,
  FinishStatus,
  PropertyStatus,
} from "../../types/property";
import {useTownshipsByRegion} from "../../hooks/useTownship";

// Create a form-specific interface that allows undefined for required number fields
interface PropertyFormData {
  titleEng: string;
  titleMm: string;
  price?: number | undefined;
  listingType: ListingType;
  developmentType: DevelopmentType;
  finishStatus: FinishStatus;
  bankStatus: boolean;
  bedrooms?: number | undefined;
  bathrooms?: number | undefined;
  descriptionEng: string;
  descriptionMm: string;
  regionId?: number | undefined;
  townshipId?: number | undefined;
  propertyTypeId?: number | undefined;
  status: PropertyStatus;
  sizeWidth?: string;
  sizeHeight?: string;
  sizeArea?: string;
  latitude?: number | undefined;
  longitude?: number | undefined;
  mapCoordinates?: string;
}

const PropertyCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const {success, error} = useToast();

  // ✅ Use form-specific interface that allows undefined values
  const [formData, setFormData] = useState<PropertyFormData>({
    titleEng: "",
    titleMm: "",
    price: undefined,
    listingType: "sale",
    developmentType: "normal",
    finishStatus: "not_finished",
    bankStatus: false,
    bedrooms: undefined,
    bathrooms: undefined,
    descriptionEng: "",
    descriptionMm: "",
    regionId: undefined,
    townshipId: undefined,
    propertyTypeId: undefined,
    status: "active",
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [filePreviews, setFilePreviews] = useState<string[]>([]);

  // Use the correct hook function
  const {handleCreateWithPhotos, isCreating} = useProperties();
  const {dropdownRegions, isLoading: regionsLoading} = useRegions();
  const {townships, isLoading: townshipsLoading} = useTownshipsByRegion(
    formData.regionId || undefined
  );
  const {propertyTypes, isLoading: typesLoading} = usePropertyTypes();

  // Handle form field changes
  const handleFieldChange = (field: keyof PropertyFormData, value: any) => {
    setFormData((prev) => ({...prev, [field]: value}));

    // Clear error when field is modified
    if (errors[field]) {
      setErrors((prev) => ({...prev, [field]: ""}));
    }

    // Reset township when region changes
    if (field === "regionId") {
      setFormData((prev) => ({...prev, townshipId: undefined}));
    }
  };

  // ✅ Fixed validation - only validate required fields
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    const getPlainText = (html: string) => {
      const div = document.createElement("div");
      div.innerHTML = html;
      return div.textContent || div.innerText || "";
    };

    if (!formData.titleEng?.trim()) {
      newErrors.titleEng = "English title is required";
    }
    if (!formData.titleMm?.trim()) {
      newErrors.titleMm = "Myanmar title is required";
    }
    if (!formData.price || formData.price <= 0) {
      newErrors.price = "Valid price is required";
    }
    if (!formData.regionId) {
      newErrors.regionId = "Region is required";
    }
    if (!formData.townshipId) {
      newErrors.townshipId = "Township is required";
    }
    if (!formData.propertyTypeId) {
      newErrors.propertyTypeId = "Property type is required";
    }
    if (
      !formData.descriptionEng ||
      getPlainText(formData.descriptionEng).trim().length < 10
    ) {
      newErrors.descriptionEng =
        "English description must be at least 10 characters";
    }
    if (
      !formData.descriptionMm ||
      getPlainText(formData.descriptionMm).trim().length < 10
    ) {
      newErrors.descriptionMm =
        "Myanmar description must be at least 10 characters";
    }

    // Validate latitude and longitude ranges
    if (
      formData.latitude !== undefined &&
      (formData.latitude < -90 || formData.latitude > 90)
    ) {
      newErrors.latitude = "Latitude must be between -90 and 90";
    }
    if (
      formData.longitude !== undefined &&
      (formData.longitude < -180 || formData.longitude > 180)
    ) {
      newErrors.longitude = "Longitude must be between -180 and 180";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle file input change
  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);

      // Validate files
      const validFiles = files.filter((file) => {
        const isValidType = file.type.startsWith("image/");
        const isValidSize = file.size <= 5 * 1024 * 1024; // 5MB

        if (!isValidType) {
          error("Invalid File", `${file.name} is not a valid image file`);
          return false;
        }
        if (!isValidSize) {
          error("File Too Large", `${file.name} is larger than 5MB`);
          return false;
        }
        return true;
      });

      if (validFiles.length + selectedFiles.length > 10) {
        error("Too Many Files", "You can only upload up to 10 photos");
        return;
      }

      setSelectedFiles((prev) => [...prev, ...validFiles]);

      // Create previews
      validFiles.forEach((file) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          if (e.target?.result) {
            setFilePreviews((prev) => [...prev, e.target!.result as string]);
          }
        };
        reader.readAsDataURL(file);
      });
    }
  };

  // Remove file
  const removeFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
    setFilePreviews((prev) => prev.filter((_, i) => i !== index));
  };

  // Clear all files
  const clearFiles = () => {
    setSelectedFiles([]);
    setFilePreviews([]);
  };

  // ✅ Fixed form submission - ensure all required fields have valid values
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      error("Validation Error", "Please fix the errors and try again");
      return;
    }

    setIsSubmitting(true);

    try {
      // ✅ Create clean data object with only valid fields
      const cleanFormData: CreatePropertyDto = {
        titleEng: formData.titleEng!.trim(),
        titleMm: formData.titleMm!.trim(),
        price: Number(formData.price!),
        listingType: formData.listingType!,
        developmentType: formData.developmentType!,
        finishStatus: formData.finishStatus!,
        bankStatus: Boolean(formData.bankStatus),
        bedrooms: Number(formData.bedrooms || 0),
        bathrooms: Number(formData.bathrooms || 0),
        descriptionEng: formData.descriptionEng!.trim(),
        descriptionMm: formData.descriptionMm!.trim(),
        regionId: Number(formData.regionId!),
        townshipId: Number(formData.townshipId!),
        propertyTypeId: Number(formData.propertyTypeId!),
        status: formData.status!,
        // Optional fields - only include if they have values
        ...(formData.sizeWidth && {sizeWidth: formData.sizeWidth.trim()}),
        ...(formData.sizeHeight && {sizeHeight: formData.sizeHeight.trim()}),
        ...(formData.sizeArea && {sizeArea: formData.sizeArea.trim()}),
        ...(formData.latitude && {latitude: Number(formData.latitude)}),
        ...(formData.longitude && {longitude: Number(formData.longitude)}),
        ...(formData.mapCoordinates && {
          mapCoordinates: formData.mapCoordinates.trim(),
        }),
      };

      console.log("🚀 Submitting property creation...", {
        cleanFormData,
        filesCount: selectedFiles.length,
      });

      // Create property with photos in one request
      const result = await handleCreateWithPhotos(cleanFormData, selectedFiles);

      if (result) {
        success(
          "Success",
          `Property created successfully${
            selectedFiles.length > 0
              ? ` with ${selectedFiles.length} photos`
              : ""
          }`
        );

        // Clear form and navigate
        clearFiles();
        navigate(ROUTES.PROPERTIES);
      }
    } catch (err) {
      console.error("❌ Property creation failed:", err);
      error("Error", "Failed to create property. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    navigate(ROUTES.PROPERTIES);
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <PageHeader
        title="Create Property"
        breadcrumbs={[
          {label: "Dashboard", path: "/"},
          {label: "Properties", path: ROUTES.PROPERTIES},
          {label: "Create"},
        ]}
      />

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Information */}
            <Card className="p-6">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                Basic Information
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  name="titleEng"
                  label="Title (English)"
                  type="text"
                  value={formData.titleEng || ""}
                  onChange={(value) => handleFieldChange("titleEng", value)}
                  error={errors.titleEng || ""}
                  required
                  placeholder="Enter property title in English"
                />

                <FormField
                  name="titleMm"
                  label="Title (Myanmar)"
                  type="text"
                  value={formData.titleMm || ""}
                  onChange={(value) => handleFieldChange("titleMm", value)}
                  error={errors.titleMm || ""}
                  required
                  placeholder="Enter property title in Myanmar"
                />

                <FormField
                  name="price"
                  label="Price (MMK)"
                  type="number"
                  value={formData.price || 0}
                  onChange={(value) =>
                    handleFieldChange("price", Number(value) || undefined)
                  }
                  error={errors.price || ""}
                  required
                  min={1}
                  placeholder="Enter price in Myanmar Kyats"
                />

                <FormField
                  name="listingType"
                  label="Listing Type"
                  type="select"
                  value={formData.listingType || ""}
                  onChange={(value) => handleFieldChange("listingType", value)}
                  required
                  options={[
                    {value: "", label: "Select listing type", disabled: true},
                    {value: "sale", label: "For Sale"},
                    {value: "rent", label: "For Rent"},
                  ]}
                />

                <FormField
                  name="developmentType"
                  label="Development Type"
                  type="select"
                  value={formData.developmentType || ""}
                  onChange={(value) =>
                    handleFieldChange("developmentType", value)
                  }
                  required
                  options={[
                    {
                      value: "",
                      label: "Select development type",
                      disabled: true,
                    },
                    {value: "normal", label: "Normal Development"},
                    {value: "new", label: "New Development"},
                  ]}
                />

                <FormField
                  name="finishStatus"
                  label="Finish Status"
                  type="select"
                  value={formData.finishStatus || ""}
                  onChange={(value) => handleFieldChange("finishStatus", value)}
                  required
                  options={[
                    {value: "", label: "Select finish status", disabled: true},
                    {value: "not_finished", label: "Not Finished"},
                    {value: "partly_finished", label: "Partly Finished"},
                    {value: "totally_finished", label: "Totally Finished"},
                  ]}
                />

                <FormField
                  name="bedrooms"
                  label="Bedrooms"
                  type="number"
                  value={formData.bedrooms || 0}
                  onChange={(value) =>
                    handleFieldChange("bedrooms", Number(value) || undefined)
                  }
                  min={0}
                  max={20}
                  placeholder="Number of bedrooms"
                />

                <FormField
                  name="bathrooms"
                  label="Bathrooms"
                  type="number"
                  value={formData.bathrooms || 0}
                  onChange={(value) =>
                    handleFieldChange("bathrooms", Number(value) || undefined)
                  }
                  min={0}
                  max={20}
                  placeholder="Number of bathrooms"
                />
              </div>
            </Card>

            {/* Location Information */}
            <Card className="p-6">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
                <MapPin className="w-5 h-5 mr-2" />
                Location Information
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <FormField
                  name="regionId"
                  label="Region"
                  type="select"
                  value={formData.regionId || ""}
                  onChange={(value) =>
                    handleFieldChange("regionId", Number(value) || undefined)
                  }
                  error={errors.regionId || ""}
                  required
                  disabled={regionsLoading}
                  options={[
                    {value: "", label: "Select Region", disabled: true},
                    ...dropdownRegions.map((region) => ({
                      value: region.id,
                      label: region.nameEng,
                    })),
                  ]}
                />

                <FormField
                  name="townshipId"
                  label="Township"
                  type="select"
                  value={formData.townshipId || ""}
                  onChange={(value) =>
                    handleFieldChange("townshipId", Number(value) || undefined)
                  }
                  error={errors.townshipId || ""}
                  required
                  disabled={!formData.regionId || townshipsLoading}
                  options={[
                    {value: "", label: "Select Township", disabled: true},
                    ...townships.map((township) => ({
                      value: township.id,
                      label: township.nameEng,
                    })),
                  ]}
                />

                <FormField
                  name="propertyTypeId"
                  label="Property Type"
                  type="select"
                  value={formData.propertyTypeId || ""}
                  onChange={(value) =>
                    handleFieldChange(
                      "propertyTypeId",
                      Number(value) || undefined
                    )
                  }
                  error={errors.propertyTypeId || ""}
                  required
                  disabled={typesLoading}
                  options={[
                    {value: "", label: "Select Type", disabled: true},
                    ...propertyTypes.map((type) => ({
                      value: type.id,
                      label: `${type.icon || ""} ${type.nameEng}`.trim(),
                    })),
                  ]}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                <FormField
                  name="latitude"
                  label="Latitude"
                  type="number"
                  value={formData.latitude || 0}
                  onChange={(value) =>
                    handleFieldChange("latitude", Number(value) || undefined)
                  }
                  placeholder="16.7967129"
                  step={0.0000001}
                />

                <FormField
                  name="longitude"
                  label="Longitude"
                  type="number"
                  value={formData.longitude || 0}
                  onChange={(value) =>
                    handleFieldChange("longitude", Number(value) || undefined)
                  }
                  placeholder="96.1609916"
                  step={0.0000001}
                />

                <FormField
                  name="mapCoordinates"
                  label="Map Coordinates"
                  type="text"
                  value={formData.mapCoordinates || ""}
                  onChange={(value) =>
                    handleFieldChange("mapCoordinates", value)
                  }
                  placeholder="16.7967129, 96.1609916"
                />
              </div>
            </Card>

            {/* Size Information */}
            <Card className="p-6">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                Size Information
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <FormField
                  name="sizeWidth"
                  label="Width"
                  type="text"
                  value={formData.sizeWidth || ""}
                  onChange={(value) => handleFieldChange("sizeWidth", value)}
                  placeholder="40ft"
                />

                <FormField
                  name="sizeHeight"
                  label="Height"
                  type="text"
                  value={formData.sizeHeight || ""}
                  onChange={(value) => handleFieldChange("sizeHeight", value)}
                  placeholder="60ft"
                />

                <FormField
                  name="sizeArea"
                  label="Area"
                  type="text"
                  value={formData.sizeArea || ""}
                  onChange={(value) => handleFieldChange("sizeArea", value)}
                  placeholder="2400 sq ft"
                />
              </div>
            </Card>

            {/* Description */}
            <Card className="p-6 bg-gradient-to-br from-blue-50 via-white to-indigo-50 dark:from-gray-800 dark:via-gray-900 dark:to-blue-900/20 border-2 border-blue-100 dark:border-blue-800/30">
              <div className="space-y-8">
                {/* English Description */}
                <div className="relative">
                  <div className="flex items-center mb-4">
                    <div className="flex items-center space-x-2">
                      <div className="w-6 h-6 bg-gradient-to-r from-green-400 to-blue-500 rounded-full flex items-center justify-center">
                        <span className="text-white text-xs font-bold">EN</span>
                      </div>
                      <h4 className="text-lg font-medium text-gray-800 dark:text-gray-200">
                        English Description
                      </h4>
                      <span className="text-red-500 text-lg">*</span>
                    </div>
                    {formData.descriptionEng && (
                      <div className="ml-auto flex items-center space-x-2 text-sm text-gray-500">
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        <span>
                          {(() => {
                            const div = document.createElement("div");
                            div.innerHTML = formData.descriptionEng;
                            const textLength = (
                              div.textContent ||
                              div.innerText ||
                              ""
                            ).length;
                            return `${textLength} characters`;
                          })()}
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="relative group">
                    <div className="absolute inset-0 bg-gradient-to-r from-blue-200 to-indigo-200 dark:from-blue-600/20 dark:to-indigo-600/20 rounded-lg blur opacity-30 group-hover:opacity-50 transition-opacity"></div>
                    <div className="relative bg-white dark:bg-gray-800 rounded-lg border-2 border-gray-200 dark:border-gray-600 focus-within:border-blue-500 dark:focus-within:border-blue-400 transition-colors">
                      <FormField
                        name="descriptionEng"
                        label=""
                        type="richtext"
                        value={formData.descriptionEng || ""}
                        onChange={(value) =>
                          handleFieldChange("descriptionEng", value)
                        }
                        error={errors.descriptionEng || ""}
                        required
                        placeholder="Write a detailed description of the property in English. Include key features, location benefits, amenities, and what makes this property special..."
                      />
                    </div>
                  </div>

                  {errors.descriptionEng && (
                    <div className="mt-2 flex items-center space-x-2 text-red-600 dark:text-red-400">
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      <span className="text-sm font-medium">
                        {errors.descriptionEng}
                      </span>
                    </div>
                  )}
                </div>

                {/* Myanmar Description */}
                <div className="relative">
                  <div className="flex items-center mb-4">
                    <div className="flex items-center space-x-2">
                      <div className="w-6 h-6 bg-gradient-to-r from-yellow-400 to-red-500 rounded-full flex items-center justify-center">
                        <span className="text-white text-xs font-bold">မြ</span>
                      </div>
                      <h4 className="text-lg font-medium text-gray-800 dark:text-gray-200">
                        Myanmar Description
                      </h4>
                      <span className="text-red-500 text-lg">*</span>
                    </div>
                    {formData.descriptionMm && (
                      <div className="ml-auto flex items-center space-x-2 text-sm text-gray-500">
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        <span>
                          {(() => {
                            const div = document.createElement("div");
                            div.innerHTML = formData.descriptionMm;
                            const textLength = (
                              div.textContent ||
                              div.innerText ||
                              ""
                            ).length;
                            return `${textLength} characters`;
                          })()}
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="relative group">
                    <div className="absolute inset-0 bg-gradient-to-r from-yellow-200 to-red-200 dark:from-yellow-600/20 dark:to-red-600/20 rounded-lg blur opacity-30 group-hover:opacity-50 transition-opacity"></div>
                    <div className="relative bg-white dark:bg-gray-800 rounded-lg border-2 border-gray-200 dark:border-gray-600 focus-within:border-yellow-500 dark:focus-within:border-yellow-400 transition-colors">
                      <FormField
                        name="descriptionMm"
                        label=""
                        type="richtext"
                        value={formData.descriptionMm || ""}
                        onChange={(value) =>
                          handleFieldChange("descriptionMm", value)
                        }
                        error={errors.descriptionMm || ""}
                        required
                        placeholder="အိမ်၊ လုံးချင်း၊ ကွန်ဒို နှင့် ခြံကြီး အကြောင်း အသေးစိတ် ရေးပြီး ဝန်ဆောင်မှုများ၊ တည်နေရာ အားသာချက်များ နှင့် အထူးဂုဏ်သတ္တိများ ထည့်သွင်း ဖော်ပြပါ..."
                      />
                    </div>
                  </div>

                  {errors.descriptionMm && (
                    <div className="mt-2 flex items-center space-x-2 text-red-600 dark:text-red-400">
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      <span className="text-sm font-medium">
                        {errors.descriptionMm}
                      </span>
                    </div>
                  )}
                </div>

                {/* Description Guidelines */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 rounded-lg p-4 border border-blue-200 dark:border-blue-700">
                  <div className="flex items-start space-x-3">
                    <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <svg
                        className="w-4 h-4 text-white"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                    </div>
                    <div>
                      <h5 className="font-medium text-blue-900 dark:text-blue-100 mb-2">
                        Writing Great Property Descriptions
                      </h5>
                      <ul className="text-sm text-blue-800 dark:text-blue-200 space-y-1">
                        <li className="flex items-center space-x-2">
                          <span className="w-1.5 h-1.5 bg-blue-500 rounded-full"></span>
                          <span>
                            Highlight unique features and selling points
                          </span>
                        </li>
                        <li className="flex items-center space-x-2">
                          <span className="w-1.5 h-1.5 bg-blue-500 rounded-full"></span>
                          <span>
                            Mention nearby amenities and transportation
                          </span>
                        </li>
                        <li className="flex items-center space-x-2">
                          <span className="w-1.5 h-1.5 bg-blue-500 rounded-full"></span>
                          <span>Include room layouts and special features</span>
                        </li>
                        <li className="flex items-center space-x-2">
                          <span className="w-1.5 h-1.5 bg-blue-500 rounded-full"></span>
                          <span>
                            Minimum 10 characters required for each language
                          </span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </Card>

            {/* Photo Upload */}
            <Card className="p-6">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
                <Upload className="w-5 h-5 mr-2" />
                Property Photos
              </h3>

              {/* File Upload Area */}
              <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 text-center">
                <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                  Upload Property Photos
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                  Select up to 10 photos (Max 5MB each) • First photo will be
                  the primary image
                </p>
                <input
                  type="file"
                  id="property-photos"
                  multiple
                  accept="image/*"
                  onChange={handleFileInputChange}
                  className="hidden"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() =>
                    document.getElementById("property-photos")?.click()
                  }
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Choose Photos
                </Button>
              </div>

              {/* Selected Photos Preview */}
              {selectedFiles.length > 0 && (
                <div className="mt-6">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-medium text-gray-900 dark:text-gray-100">
                      Selected Photos ({selectedFiles.length}/10)
                    </h4>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={clearFiles}
                    >
                      Clear All
                    </Button>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {selectedFiles.map((file, index) => (
                      <div key={index} className="relative group">
                        <div className="aspect-square bg-gray-100 dark:bg-gray-800 rounded-lg overflow-hidden">
                          {filePreviews[index] ? (
                            <img
                              src={filePreviews[index]}
                              alt={`Preview ${index + 1}`}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Image className="w-8 h-8 text-gray-400" />
                            </div>
                          )}
                        </div>

                        {/* Primary Photo Badge */}
                        {index === 0 && (
                          <Badge
                            variant="info"
                            className="absolute top-2 left-2 text-xs"
                          >
                            Primary
                          </Badge>
                        )}

                        {/* Remove Button */}
                        <Button
                          type="button"
                          variant="danger"
                          size="sm"
                          className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={() => removeFile(index)}
                        >
                          <X className="w-3 h-3" />
                        </Button>

                        {/* File Info */}
                        <div className="mt-2">
                          <p className="text-xs text-gray-600 dark:text-gray-400 truncate">
                            {file.name}
                          </p>
                          <p className="text-xs text-gray-500">
                            {(file.size / 1024 / 1024).toFixed(1)} MB
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Status & Settings */}
            <Card className="p-6">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                Status & Settings
              </h3>

              <div className="space-y-4">
                <FormField
                  name="status"
                  label="Status"
                  type="select"
                  value={formData.status || ""}
                  onChange={(value) => handleFieldChange("status", value)}
                  options={[
                    {value: "active", label: "Active"},
                    {value: "inactive", label: "Inactive"},
                  ]}
                />

                <FormField
                  name="bankStatus"
                  label="Bank Financing Available"
                  type="checkbox"
                  checked={formData.bankStatus ?? false}
                  onChange={(checked) =>
                    handleFieldChange("bankStatus", checked)
                  }
                  checkboxLabel="Yes, bank financing is available"
                />
              </div>
            </Card>

            {/* Form Actions */}
            <Card className="p-6">
              <div className="space-y-3">
                <Button
                  type="submit"
                  variant="primary"
                  className="w-full"
                  loading={isSubmitting || isCreating}
                  disabled={isSubmitting || isCreating}
                >
                  <Save className="w-4 h-4 mr-2" />
                  {isSubmitting || isCreating
                    ? "Creating..."
                    : "Create Property"}
                </Button>

                <Button
                  type="button"
                  variant="secondary"
                  className="w-full"
                  onClick={handleCancel}
                  disabled={isSubmitting || isCreating}
                >
                  <X className="w-4 h-4 mr-2" />
                  Cancel
                </Button>
              </div>
            </Card>

            {/* Preview */}
            {formData.titleEng && (
              <Card className="p-6">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                  Preview
                </h3>

                <div className="space-y-3">
                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-white">
                      {formData.titleEng}
                    </h4>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {formData.titleMm}
                    </p>
                  </div>

                  {formData.price && formData.price > 0 && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">Price:</span>
                      <div className="text-right">
                        <p className="font-semibold text-gray-900 dark:text-white">
                          {new Intl.NumberFormat("en-US").format(
                            formData.price
                          )}{" "}
                          MMK
                        </p>
                        <Badge
                          variant={
                            formData.listingType === "sale" ? "success" : "info"
                          }
                          size="sm"
                        >
                          {formData.listingType === "sale"
                            ? "For Sale"
                            : "For Rent"}
                        </Badge>
                      </div>
                    </div>
                  )}

                  {(formData.bedrooms || formData.bathrooms) && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500">Rooms:</span>
                      <span className="text-gray-900 dark:text-white">
                        {formData.bedrooms || 0} bed, {formData.bathrooms || 0}{" "}
                        bath
                      </span>
                    </div>
                  )}

                  {selectedFiles.length > 0 && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500">Photos:</span>
                      <span className="text-gray-900 dark:text-white">
                        {selectedFiles.length} selected
                      </span>
                    </div>
                  )}
                </div>
              </Card>
            )}

            {/* Loading Progress */}
            {(isSubmitting || isCreating) && (
              <Card className="p-4 bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
                <div className="flex items-center space-x-3">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                  <div>
                    <p className="text-sm font-medium text-blue-800 dark:text-blue-200">
                      Creating Property...
                    </p>
                    <p className="text-xs text-blue-600 dark:text-blue-300">
                      {selectedFiles.length > 0
                        ? `Uploading property data with ${selectedFiles.length} photos...`
                        : "Uploading property data..."}
                    </p>
                  </div>
                </div>
              </Card>
            )}
          </div>
        </div>
      </form>
    </div>
  );
};

export default PropertyCreatePage;
